import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';

import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { AnalyticsService } from 'src/app/components/logging/analytics.service';
import { ComponentsService } from '../../services/components.service';
import {
  GeneratedLayoutData,
  GenerateLayoutResponse,
  LayoutAiApiService,
} from './services/layout-ai-api.service';

export type LayoutAiMessageType = 'default' | 'no_credits' | 'error';

export interface LayoutAiShowOptions {
  component: string;
  componentId: string;
  onGenerated: (layout: GeneratedLayoutData) => void;
}

const PLACEHOLDER_EXAMPLES: Record<string, string> = {
  calendar: 'a list of events with the time on the left',
  counter: 'large numbers inside circles with the labels below',
  date: 'the day of the week above the full date',
  rss: 'two columns of cards with the image on the left',
  table: 'a grid of profile cards with the photo on top',
  time: 'a large digital clock with small seconds',
  weather: 'the current temperature above a row of daily forecasts',
};

@Component({
  selector: 'template-component-layout-ai',
  templateUrl: './layout-ai.component.html',
  styleUrls: ['./layout-ai.component.scss'],
  standalone: false,
})
export class LayoutAiComponent implements OnInit, OnDestroy {
  static readonly TYPE = 'rise-layout-generate-ai';

  component: string;
  componentId: string;
  prompt = '';
  loading = false;
  messageType: LayoutAiMessageType = 'default';

  private onGenerated: ((layout: GeneratedLayoutData) => void) | null = null;
  private activeRequestId = 0;

  constructor(
    private elementRef: ElementRef,
    private componentsService: ComponentsService,
    private layoutAiApi: LayoutAiApiService,
    private companyStateService: CompanyStateService,
    private userStateService: UserStateService,
    private analyticsFactory: AnalyticsService,
  ) {}

  ngOnInit(): void {
    this.componentsService.registerDirective({
      type: LayoutAiComponent.TYPE,
      element: this.elementRef.nativeElement,
      usesBackTitleHeader: () => true,
      show: (options: LayoutAiShowOptions) => {
        this.activeRequestId++;
        this.component = options.component;
        this.componentId = options.componentId;
        this.onGenerated = options.onGenerated;
        this.prompt = '';
        this.loading = false;
        this.messageType = 'default';
      },
      onBackHandler: () => {
        this.activeRequestId++;
        this.loading = false;
        this.onGenerated = null;
        return false;
      },
    });
  }

  ngOnDestroy(): void {
    this.activeRequestId++;
  }

  get placeholder(): string {
    const prompt = 'Describe the layout you want.';
    const example = PLACEHOLDER_EXAMPLES[this.component];

    return example ? `${prompt} e.g. ${example}` : prompt;
  }

  canGenerate(): boolean {
    return !!this.prompt.trim() && !this.loading;
  }

  canAccessCompanySettings(): boolean {
    return (
      this.userStateService.isRiseAdmin() || this.userStateService.isUserAdmin()
    );
  }

  isErrorMessage(): boolean {
    return this.messageType === 'error';
  }

  async onGenerate(): Promise<void> {
    if (!this.canGenerate()) {
      return;
    }

    this.loading = true;
    this.messageType = 'default';
    const requestId = ++this.activeRequestId;
    const prompt = this.prompt.trim();
    const startedAt = Date.now();

    try {
      const response = await this.layoutAiApi.generateLayout({
        component: this.component,
        prompt,
      });

      if (this.isStaleRequest(requestId)) {
        return;
      }

      this.loading = false;

      if (response.type === 'no_credits') {
        this.messageType = 'no_credits';
        this.trackGeneration('no_credits', startedAt, response);
      } else {
        this.messageType = 'default';
        this.trackGeneration('success', startedAt, response);
        this.onGenerated?.(response.data);
        this.componentsService.onBackButton();
      }
    } catch {
      if (this.isStaleRequest(requestId)) {
        return;
      }

      this.loading = false;
      this.messageType = 'error';
      this.trackGeneration('error', startedAt);
    }
  }

  // Selecting another component in the preview leaves the page without the back
  // button, so also check that the page is still the one being edited.
  private isStaleRequest(requestId: number): boolean {
    return (
      requestId !== this.activeRequestId ||
      this.componentsService.selected?.type !== LayoutAiComponent.TYPE
    );
  }

  private trackGeneration(
    status: 'success' | 'no_credits' | 'error',
    startedAt: number,
    response?: GenerateLayoutResponse,
  ): void {
    this.analyticsFactory.track('AI Layout Generate', {
      status,
      component: this.component,
      componentId: this.componentId,
      companyId: this.companyStateService.getSelectedCompanyId(),
      creditsUsed: response?.creditsUsed,
      durationMs: Date.now() - startedAt,
    });
  }
}
